import React, { useState, useEffect } from 'react';
import api, { getImageUrl } from '../../utils/api';

interface IconData {
    _id: string;
    name: string;
    icon: string;
}

interface IconLibraryModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSelect: (iconUrl: string) => void;
}

const IconLibraryModal: React.FC<IconLibraryModalProps> = ({ isOpen, onClose, onSelect }) => {
    const [icons, setIcons] = useState<IconData[]>([]);
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');

    useEffect(() => {
        if (isOpen) {
            fetchIcons();
        }
    }, [isOpen]);

    const fetchIcons = async () => {
        try {
            setLoading(true);
            const data = await api.getIcons();
            setIcons(data);
        } catch (error) {
            console.error('Error fetching icons:', error);
        } finally {
            setLoading(false);
        }
    };

    const filteredIcons = icons.filter(icon => 
        icon.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm transition-opacity" onClick={onClose}>
            <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden animate-fade-in-up" onClick={e => e.stopPropagation()}>
                
                {/* Header */}
                <div className="px-6 py-4 border-b border-slate-100 flex justify-between items-center bg-slate-50 shrink-0">
                    <div>
                        <h3 className="text-lg font-bold text-[#05144b]">Select Icon</h3>
                        <p className="text-[11px] font-medium text-slate-500">Choose an icon from your library</p>
                    </div>
                    <button onClick={onClose} className="text-slate-400 hover:text-rose-500 transition-colors w-8 h-8 flex items-center justify-center rounded-lg hover:bg-rose-50">
                        <i className="fa-solid fa-xmark text-lg"></i>
                    </button>
                </div>

                {/* Search Bar */}
                <div className="p-4 border-b border-slate-100 shrink-0">
                    <div className="relative">
                        <i className="fa-solid fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-sm"></i>
                        <input
                            type="text"
                            placeholder="Search icons by name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-2.5 text-sm text-[#05144b] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 font-medium"
                        />
                    </div>
                </div>

                {/* Icon Grid */}
                <div className="flex-1 overflow-y-auto p-4 bg-slate-50/50">
                    {loading ? (
                        <div className="flex justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        </div>
                    ) : filteredIcons.length === 0 ? (
                        <div className="text-center py-12">
                            <i className="fa-solid fa-icons text-4xl text-slate-200 mb-3"></i>
                            <p className="text-slate-500 font-medium text-sm">No icons found matching "{searchQuery}"</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                            {filteredIcons.map(icon => (
                                <button
                                    key={icon._id}
                                    onClick={() => {
                                        onSelect(icon.icon);
                                        onClose();
                                    }}
                                    className="bg-white border border-slate-200 hover:border-blue-500 rounded-xl p-3 flex flex-col items-center gap-2 hover:shadow-md transition-all group"
                                >
                                    <div className="w-10 h-10 flex items-center justify-center overflow-hidden">
                                        <img 
                                            src={getImageUrl(icon.icon)} 
                                            alt={icon.name} 
                                            className="max-w-full max-h-full object-contain group-hover:scale-110 transition-transform"
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.style.display = 'none';
                                                target.parentElement?.insertAdjacentHTML('beforeend', '<i class="fa-solid fa-image text-slate-300"></i>');
                                            }}
                                        />
                                    </div>
                                    <span className="text-[10px] font-bold text-slate-600 text-center w-full truncate" title={icon.name}>{icon.name}</span>
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            <style>{`
                .animate-fade-in-up {
                    animation: fadeInUp 0.2s ease-out forwards;
                }
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(10px) scale(0.98); }
                    to { opacity: 1; transform: translateY(0) scale(1); }
                }
            `}</style>
        </div>
    );
};

export default IconLibraryModal;
